import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SafewardClient, SafewardHttpError } from '../safeward-client.js'
import type { AppSettings, ZerionPortfolio } from '../types.js'

export type PortfolioState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; portfolio: ZerionPortfolio; refreshedAt: Date }
  | { status: 'error'; message: string; isUnavailable: boolean }

export interface UsePortfolio {
  state: PortfolioState
  refresh: (force?: boolean) => Promise<void>
}

const HOUR_MS = 60 * 60 * 1000

/**
 * Pulls the Zerion-backed portfolio snapshot for the given address. The
 * worker KV-caches at ~1h, so we mirror that on the client: one fetch on
 * mount, then a refresh every hour. `refresh(true)` busts the worker cache.
 */
export function usePortfolio(
  settings: AppSettings,
  address: string | null,
): UsePortfolio {
  const [state, setState] = useState<PortfolioState>({ status: 'idle' })

  const client = useMemo(
    () =>
      new SafewardClient({
        baseUrl: settings.safewardUrl,
        authToken: settings.safewardAuthToken,
      }),
    [settings.safewardUrl, settings.safewardAuthToken],
  )
  const clientRef = useRef(client)
  clientRef.current = client

  const addressRef = useRef(address)
  addressRef.current = address

  const refresh = useCallback(async (force = false) => {
    const addr = addressRef.current
    if (!addr) {
      setState({ status: 'idle' })
      return
    }
    setState((prev) =>
      prev.status === 'ready' ? prev : { status: 'loading' },
    )
    try {
      const portfolio = await clientRef.current.portfolio(addr, {
        refresh: force,
      })
      setState({ status: 'ready', portfolio, refreshedAt: new Date() })
    } catch (err) {
      const isUnavailable =
        err instanceof SafewardHttpError && err.status === 503
      setState({
        status: 'error',
        message: (err as Error).message,
        isUnavailable,
      })
    }
  }, [])

  useEffect(() => {
    void refresh(false)
  }, [refresh, address])

  // Hourly refresh — the worker KV-caches at the same cadence, so this
  // mostly just rolls our local snapshot forward to the freshest cache.
  useEffect(() => {
    if (!address) return
    const t = setInterval(() => void refresh(false), HOUR_MS)
    return () => clearInterval(t)
  }, [refresh, address])

  return { state, refresh }
}
